import type { IdentityState } from '@sisumail/types';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { api, type Validity } from '../lib/api';
import { logger } from '../lib/logger';

interface AuthContextType {
    username: string | null;
    state: IdentityState | null;
    subscriptionExpiresAt: string | null;
    validity: Validity;
    privateKey: Uint8Array | null;
    isLoading: boolean;
    isLoggingOut: boolean;
    login: (username: string, key: Uint8Array) => Promise<void>;
    logout: () => Promise<void>;
    forgetDevice: () => Promise<void>;
    refreshSession: () => Promise<void>;
    completeRegistration: (username: string, key: Uint8Array) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [username, setUsername] = useState<string | null>(null);
    const [state, setState] = useState<IdentityState | null>(null);
    const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState<string | null>(null);
    const [validity, setValidity] = useState<Validity>('unknown');
    const [privateKey, setPrivateKey] = useState<Uint8Array | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const clearAuthState = useCallback(() => {
        setUsername(null);
        setState(null);
        setValidity('unknown');
        setPrivateKey(null);
    }, []);

    const checkSession = useCallback(async () => {
        if (isLoggingOut) return;

        try {
            const { data: status, validity: statusValidity } = await api.identity.status();

            let savedKey: Uint8Array | null = null;
            if (status.username) {
                try {
                    const { secureStorage } = await import('../lib/db');
                    const key = await secureStorage.getKey(status.username);
                    if (key instanceof Uint8Array) {
                        savedKey = key;
                    }
                } catch (e) {
                    logger.captureError('IndexedDB key read failed', e, {
                        username: status.username,
                    });
                }
            }

            setUsername(status.username);
            setState(status.state as IdentityState);
            setSubscriptionExpiresAt(
                status.subscription_expires_at !== null && status.subscription_expires_at !== undefined
                    ? String(status.subscription_expires_at)
                    : null
            );
            setValidity(statusValidity);
            setPrivateKey(savedKey);
        } catch {
            clearAuthState();
        } finally {
            setIsLoading(false);
        }
    }, [isLoggingOut, clearAuthState]);

    useEffect(() => {
        checkSession();
    }, [checkSession]);

    const login = useCallback(
        async (user: string, key: Uint8Array) => {
            setIsLoading(true);
            try {
                // 1. Get Challenge
                const {
                    data: { nonce },
                } = await api.auth.challenge({ identity_id: user });

                // 2. Sign Challenge
                const { signWithEd25519 } = await import('@sisumail/crypto');
                const signature = await signWithEd25519(key, nonce);

                // 3. Prove
                await api.auth.prove({
                    identity_id: user,
                    nonce,
                    signature,
                });

                // 4. Set Local State
                setUsername(user);
                setPrivateKey(key);

                // 5. Persist key
                try {
                    const { secureStorage } = await import('../lib/db');
                    await secureStorage.storeKey(user, key);
                } catch (_e) { }

                // 6. Refresh Status
                await checkSession();
            } finally {
                setIsLoading(false);
            }
        },
        [checkSession]
    );

    const logout = useCallback(async () => {
        setIsLoggingOut(true);
        clearAuthState();

        try {
            await api.auth.logout();
        } catch (_e) {
        } finally {
            setIsLoggingOut(false);
        }
    }, [clearAuthState]);

    const forgetDevice = useCallback(async () => {
        const currentUsername = username;

        setIsLoggingOut(true);
        clearAuthState();

        if (currentUsername) {
            try {
                const { secureStorage } = await import('../lib/db');
                await secureStorage.deleteKey(currentUsername);
            } catch (_e) { }
        }

        try {
            await api.auth.logout();
        } catch (_e) {
        } finally {
            setIsLoggingOut(false);
        }
    }, [username, clearAuthState]);

    const refreshSession = useCallback(async () => {
        await checkSession();
    }, [checkSession]);

    // Optimized flow for after registration (session cookie already set by API)
    const completeRegistration = useCallback(
        async (user: string, key: Uint8Array) => {
            setIsLoading(true);
            try {
                // 1. Set Local State
                setUsername(user);
                setPrivateKey(key);

                // 2. Persist key
                try {
                    const { secureStorage } = await import('../lib/db');
                    await secureStorage.storeKey(user, key);
                } catch (_e) { }

                // 3. Refresh Status (verify session matches)
                await checkSession();
            } finally {
                setIsLoading(false);
            }
        },
        [checkSession]
    );

    return (
        <AuthContext.Provider
            value={{
                username,
                state,
                subscriptionExpiresAt,
                validity,
                privateKey,
                isLoading,
                isLoggingOut,
                login,
                logout,
                forgetDevice,
                refreshSession,
                completeRegistration,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
